import { css } from '@emotion/react';
import { BREAKPOINTS, ScreenReaderOnly, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import Skeleton from 'react-loading-skeleton';
import { Flight, isSameDate } from 'shared';

import { getFlightStopText, getJourneyDuration } from '../../../utils';

import { mapFlightTimeData } from './helpers';
import { SegmentDetailsIcon } from './SegmentDetailsIcon';

const styles = {
  container: (isResponsive: boolean) => css`
    width: 100%;
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: ${isResponsive ? 'space-between' : 'flex-start'};

    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      justify-content: flex-start;
    }
  `,
  btn: css`
    all: unset;
    display: block;
  `,
  time: css`
    color: ${semanticTokens.color.text.primary};
    font-size: ${tokens.fontSize.paragraph['paragraph-5']};
    font-weight: 700;
    display: inline-flex;
    margin: 0;
    gap: 8px;

    @container main-container (max-width: ${BREAKPOINTS.layout.md + 1}px) {
      font-size: ${tokens.fontSize.paragraph['paragraph-4']};
    }
  `,
  dash: (isResponsive: boolean) => css`
    &:before {
      content: '-';
      display: inline-block;
    }

    ${isResponsive &&
    `
      display: none;
      @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
        display: inline-block;
      }
    `}
  `,
  stops: (isResponsive: boolean) => css`
    width: 100%;
    min-width: 180px;
    display: flex;
    gap: 4px;
    align-items: center;
    color: ${semanticTokens.color.text.secondary};
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};

    justify-content: ${isResponsive ? 'center' : 'flex-start'};
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      font-size: ${tokens.fontSize.paragraph['paragraph-3']};
      justify-content: flex-start;
    }

    span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      min-width: 0;
      max-width: 100%;
    }
  `,
  stopsMobile: (isResponsive: boolean) => css`
    display: ${isResponsive ? 'flex' : 'none'};
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      display: none;
    }
  `,
  stopsDesktop: (isResponsive: boolean) => css`
    display: ${isResponsive ? 'none' : 'flex'};
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      display: flex;
    }
  `,
};

export function FlightTimesAndStops({ flight, isResponsive = true }: { flight: Flight; isResponsive?: boolean }) {
  const data = mapFlightTimeData(flight);
  const { departureDateTime, arrivalDateTime, stops, screenReaderString } = data;

  if (!departureDateTime.isValid || !arrivalDateTime.isValid) {
    return null;
  }

  const duration = getJourneyDuration(departureDateTime, arrivalDateTime);
  const sameDate = isSameDate(departureDateTime, arrivalDateTime);

  const { hours, minutes } = duration;

  const departureTime = departureDateTime.toFormat('HH:mm');
  const arrivalTime = `${arrivalDateTime.toFormat('HH:mm')}${sameDate ? '' : ' +1'}`;

  const stopsStr = `${hours} h${minutes > 0 ? ` ${minutes} min` : ''} | ${getFlightStopText(stops)}`;

  return (
    <>
      <ScreenReaderOnly as="span">{screenReaderString}</ScreenReaderOnly>

      <div aria-hidden="true">
        <div css={styles.container(isResponsive)} aria-label={screenReaderString}>
          {isResponsive ? (
            <button className="flight-row-focus" data-testid="upsell-flight-row-button" css={styles.btn} type="button">
              <div css={styles.time}>
                <time dateTime={departureDateTime.toISO()}>{departureTime}</time>
              </div>
            </button>
          ) : (
            <div css={styles.time}>
              <time dateTime={departureDateTime.toISO()}>{departureTime}</time>
            </div>
          )}

          <div css={styles.dash(isResponsive)} />

          <div css={[styles.stops(isResponsive), styles.stopsMobile(isResponsive)]}>
            <span>{stopsStr}</span>
            <SegmentDetailsIcon segments={flight.segments} />
          </div>

          <div css={styles.time}>
            <time dateTime={arrivalDateTime.toISO()}>{arrivalTime}</time>
          </div>
        </div>

        <div css={[styles.stops(isResponsive), styles.stopsDesktop(isResponsive)]}>
          <span>{stopsStr}</span>
          <SegmentDetailsIcon segments={flight.segments} />
        </div>
      </div>
    </>
  );
}

export function FlightTimesAndStopsSkeleton({ isResponsive = true }: { isResponsive?: boolean }) {
  return (
    <div>
      <div css={styles.container(isResponsive)}>
        <div css={styles.time}>
          <Skeleton width={60} height={24} />
        </div>

        <div css={styles.dash(isResponsive)} />

        <div css={[styles.stops(isResponsive), styles.stopsMobile(isResponsive)]}>
          <Skeleton width={160} height={12} />
        </div>

        <div css={styles.time}>
          <Skeleton width={60} height={24} />
        </div>
      </div>

      <div style={{ marginTop: '12px' }}>
        <div css={[styles.stops(isResponsive), styles.stopsDesktop(isResponsive)]}>
          <Skeleton width={160} height={12} />
        </div>
      </div>
    </div>
  );
}
